import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Slider } from './ui/slider';

const ColoringTherapy = ({ onComplete }) => {
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [currentColor, setCurrentColor] = useState('#3B82F6');
  const [coloredSections, setColoredSections] = useState({});
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [sessionTime, setSessionTime] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [selectedPalette, setSelectedPalette] = useState('nature');
  const [showAffirmations, setShowAffirmations] = useState(false);

  // Coloring designs with SVG paths and affirmations
  const coloringDesigns = {
    mandala: {
      name: "Peaceful Mandala",
      description: "A circular pattern representing wholeness and peace",
      icon: "🌸",
      difficulty: "Medium",
      sections: 12,
      affirmations: [
        "I am centered and balanced",
        "Peace flows through me",
        "I create harmony in my life",
        "I am connected to the present moment"
      ],
      svg: `
        <svg viewBox="0 0 200 200" className="w-full h-full">
          <g transform="translate(100,100)">
            <circle r="80" fill="none" stroke="#e5e7eb" strokeWidth="2" className="section" data-section="0"/>
            <circle r="60" fill="none" stroke="#e5e7eb" strokeWidth="2" className="section" data-section="1"/>
            <circle r="40" fill="none" stroke="#e5e7eb" strokeWidth="2" className="section" data-section="2"/>
            <circle r="20" fill="none" stroke="#e5e7eb" strokeWidth="2" className="section" data-section="3"/>
            ${Array.from({ length: 8 }, (_, i) => {
              const angle = (i * 45) * Math.PI / 180;
              return `<path d="M 0 0 L ${Math.cos(angle) * 80} ${Math.sin(angle) * 80}" stroke="#e5e7eb" strokeWidth="2" className="section" data-section="${4 + i}"/>`;
            }).join('')}
          </g>
        </svg>
      `
    },
    flower: {
      name: "Blooming Flower",
      description: "A beautiful flower representing growth and beauty",
      icon: "🌺",
      difficulty: "Easy",
      sections: 8,
      affirmations: [
        "I am growing and blooming",
        "Beauty surrounds me",
        "I embrace my natural growth",
        "I am rooted in strength"
      ],
      svg: `
        <svg viewBox="0 0 200 200" className="w-full h-full">
          <g transform="translate(100,100)">
            <circle r="10" fill="none" stroke="#e5e7eb" strokeWidth="2" className="section" data-section="0"/>
            ${Array.from({ length: 6 }, (_, i) => {
              const angle = (i * 60) * Math.PI / 180;
              return `<ellipse cx="${Math.cos(angle) * 30}" cy="${Math.sin(angle) * 30}" rx="20" ry="35" fill="none" stroke="#e5e7eb" strokeWidth="2" className="section" data-section="${1 + i}" transform="rotate(${i * 60} ${Math.cos(angle) * 30} ${Math.sin(angle) * 30})"/>`;
            }).join('')}
            <rect x="-3" y="10" width="6" height="60" fill="none" stroke="#e5e7eb" strokeWidth="2" className="section" data-section="7"/>
          </g>
        </svg>
      `
    },
    butterfly: {
      name: "Freedom Butterfly",
      description: "A butterfly symbolizing transformation and freedom",
      icon: "🦋",
      difficulty: "Medium",
      sections: 10,
      affirmations: [
        "I embrace change with grace",
        "I am free to be myself",
        "Transformation brings beauty",
        "I spread my wings with confidence"
      ],
      svg: `
        <svg viewBox="0 0 200 200" className="w-full h-full">
          <g transform="translate(100,100)">
            <ellipse cx="-30" cy="-30" rx="25" ry="40" fill="none" stroke="#e5e7eb" strokeWidth="2" className="section" data-section="0"/>
            <ellipse cx="30" cy="-30" rx="25" ry="40" fill="none" stroke="#e5e7eb" strokeWidth="2" className="section" data-section="1"/>
            <ellipse cx="-35" cy="20" rx="20" ry="30" fill="none" stroke="#e5e7eb" strokeWidth="2" className="section" data-section="2"/>
            <ellipse cx="35" cy="20" rx="20" ry="30" fill="none" stroke="#e5e7eb" strokeWidth="2" className="section" data-section="3"/>
            <rect x="-2" y="-50" width="4" height="80" fill="none" stroke="#e5e7eb" strokeWidth="2" className="section" data-section="4"/>
            <circle cx="-20" cy="-25" r="8" fill="none" stroke="#e5e7eb" strokeWidth="2" className="section" data-section="5"/>
            <circle cx="20" cy="-25" r="8" fill="none" stroke="#e5e7eb" strokeWidth="2" className="section" data-section="6"/>
            <circle cx="-25" cy="15" r="6" fill="none" stroke="#e5e7eb" strokeWidth="2" className="section" data-section="7"/>
            <circle cx="25" cy="15" r="6" fill="none" stroke="#e5e7eb" strokeWidth="2" className="section" data-section="8"/>
            <circle cx="0" cy="-60" r="4" fill="none" stroke="#e5e7eb" strokeWidth="2" className="section" data-section="9"/>
          </g>
        </svg>
      `
    },
    tree: {
      name: "Life Tree",
      description: "A tree representing growth, stability, and connection to nature",
      icon: "🌳",
      difficulty: "Hard",
      sections: 15,
      affirmations: [
        "I am grounded and strong",
        "I grow towards the light",
        "My roots run deep",
        "I provide shelter and peace"
      ],
      svg: `
        <svg viewBox="0 0 200 200" className="w-full h-full">
          <g transform="translate(100,100)">
            <rect x="-8" y="40" width="16" height="50" fill="none" stroke="#e5e7eb" strokeWidth="2" className="section" data-section="0"/>
            <circle cx="0" cy="-20" r="45" fill="none" stroke="#e5e7eb" strokeWidth="2" className="section" data-section="1"/>
            ${Array.from({ length: 8 }, (_, i) => {
              const angle = (i * 45) * Math.PI / 180;
              return `<circle cx="${Math.cos(angle) * 35}" cy="${Math.sin(angle) * 35 - 20}" r="15" fill="none" stroke="#e5e7eb" strokeWidth="2" className="section" data-section="${2 + i}"/>`;
            }).join('')}
            ${Array.from({ length: 5 }, (_, i) => {
              return `<path d="M 0 90 Q ${(i - 2) * 20} 70 ${(i - 2) * 30} 90" fill="none" stroke="#e5e7eb" strokeWidth="2" className="section" data-section="${10 + i}"/>`;
            }).join('')}
          </g>
        </svg>
      `
    }
  };

  // Color palettes based on moods/themes
  const colorPalettes = {
    nature: {
      name: "Nature Vibes",
      colors: ['#22C55E', '#16A34A', '#15803D', '#84CC16', '#65A30D', '#A3E635', '#DCFCE7'],
      mood: "Grounding and peaceful"
    },
    sunset: {
      name: "Sunset Dreams", 
      colors: ['#F97316', '#EA580C', '#DC2626', '#EF4444', '#F59E0B', '#FBBF24', '#FED7AA'],
      mood: "Warm and energizing"
    },
    ocean: {
      name: "Ocean Calm",
      colors: ['#3B82F6', '#2563EB', '#1D4ED8', '#06B6D4', '#0891B2', '#0E7490', '#BAE6FD'],
      mood: "Calm and flowing"
    },
    rainbow: {
      name: "Rainbow Joy",
      colors: ['#EF4444', '#F97316', '#EAB308', '#22C55E', '#3B82F6', '#8B5CF6', '#EC4899'],
      mood: "Joyful and creative"
    },
    pastel: {
      name: "Soft Pastels",
      colors: ['#FECACA', '#FED7AA', '#FEF3C7', '#D9F99D', '#BFDBFE', '#DDD6FE', '#FBCFE8'],
      mood: "Gentle and soothing"
    },
    earth: {
      name: "Earth Tones",
      colors: ['#92400E', '#B45309', '#D97706', '#78716C', '#57534E', '#A3A3A3', '#F5F5F4'],
      mood: "Grounded and stable"
    }
  };

  useEffect(() => {
    let timer;
    if (isActive) {
      timer = setInterval(() => {
        setSessionTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isActive]);

  useEffect(() => {
    if (selectedDesign) {
      const coloredCount = Object.keys(coloredSections).length;
      const totalSections = coloringDesigns[selectedDesign].sections;
      const percentage = (coloredCount / totalSections) * 100;
      setCompletionPercentage(percentage);
      
      // Show affirmations at milestones
      if (percentage >= 25 && percentage < 50 && !showAffirmations) {
        setShowAffirmations(true);
        setTimeout(() => setShowAffirmations(false), 3000);
      }
    }
  }, [coloredSections, selectedDesign]);

  const selectDesign = (designKey) => {
    setSelectedDesign(designKey);
    setColoredSections({});
    setCompletionPercentage(0);
    setSessionTime(0);
    setIsActive(true);
  };

  const colorSection = (sectionId) => {
    if (!selectedDesign) return;
    
    setColoredSections(prev => ({
      ...prev,
      [sectionId]: currentColor
    }));
  };

  const completeColoring = () => {
    if (onComplete) {
      onComplete({
        type: 'coloring_therapy',
        design: selectedDesign,
        completionPercentage,
        duration: sessionTime,
        colorsUsed: [...new Set(Object.values(coloredSections))].length
      });
    }
  };

  const resetColoring = () => {
    setColoredSections({});
    setCompletionPercentage(0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCurrentAffirmation = () => {
    if (!selectedDesign) return "";
    const design = coloringDesigns[selectedDesign];
    const index = Math.floor(Math.random() * design.affirmations.length);
    return design.affirmations[index];
  };

  if (!selectedDesign) {
    return (
      <div className="max-w-4xl mx-auto space-y-6" data-testid="coloring-therapy">
        <Card className="glass-card p-6 bg-gradient-to-r from-pink-50 to-purple-50">
          <div className="text-center space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">
              🎨 Coloring Therapy
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Choose a design that speaks to you. Coloring is a mindful practice that reduces stress, 
              improves focus, and promotes emotional well-being.
            </p>
          </div>
        </Card>

        <div className="grid md:grid-cols-2 gap-6">
          {Object.entries(coloringDesigns).map(([key, design]) => (
            <Card 
              key={key} 
              className="activity-card p-6 cursor-pointer"
              onClick={() => selectDesign(key)}
            >
              <div className="text-center space-y-4">
                <div className="text-4xl mb-3">{design.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900">{design.name}</h3>
                <p className="text-gray-600">{design.description}</p>
                
                <div className="flex justify-center space-x-3">
                  <Badge 
                    className={`${
                      design.difficulty === 'Easy' ? 'bg-green-100 text-green-700' :
                      design.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}
                  >
                    {design.difficulty}
                  </Badge>
                  <Badge className="bg-blue-100 text-blue-700">
                    {design.sections} sections
                  </Badge>
                </div>
                
                <Button className="btn-primary w-full">
                  Start Coloring {design.icon}
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {/* Benefits */}
        <Card className="glass-card p-6 bg-gradient-to-r from-green-50 to-amber-50">
          <h4 className="font-semibold text-green-800 mb-3">🌟 Benefits of Coloring Therapy</h4>
          <div className="grid md:grid-cols-3 gap-4 text-sm text-green-700">
            <div>
              <h5 className="font-medium mb-2">Mental Health</h5>
              <ul className="space-y-1">
                <li>• Reduces anxiety and stress</li>
                <li>• Improves focus and mindfulness</li>
                <li>• Promotes relaxation</li>
              </ul>
            </div>
            <div>
              <h5 className="font-medium mb-2">Emotional Benefits</h5>
              <ul className="space-y-1">
                <li>• Boosts self-esteem</li>
                <li>• Enhances mood</li>
                <li>• Provides emotional release</li>
              </ul>
            </div>
            <div>
              <h5 className="font-medium mb-2">Creative Expression</h5>
              <ul className="space-y-1">
                <li>• Encourages creativity</li>
                <li>• Non-verbal expression</li>
                <li>• Personal interpretation</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const currentDesign = coloringDesigns[selectedDesign];

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="coloring-therapy-active">
      {/* Header */}
      <Card className="glass-card p-6 bg-gradient-to-r from-pink-50 to-purple-50">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {currentDesign.icon} {currentDesign.name}
            </h2>
            <p className="text-gray-600">{currentDesign.description}</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-purple-600">{Math.round(completionPercentage)}%</div>
            <div className="text-sm text-gray-500">Complete</div>
            {isActive && (
              <div className="text-sm text-gray-500 mt-1">⏱️ {formatTime(sessionTime)}</div>
            )}
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-4">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-pink-400 to-purple-400 h-2 rounded-full transition-all duration-500"
              style={{ width: `${completionPercentage}%` }}
            ></div>
          </div>
        </div>
      </Card>

      {/* Affirmation Display */}
      {showAffirmations && (
        <Card className="glass-card p-4 bg-gradient-to-r from-yellow-50 to-orange-50 animate-pulse">
          <div className="text-center">
            <div className="text-2xl mb-2">✨</div>
            <p className="text-lg font-medium text-orange-800 italic">
              "{getCurrentAffirmation()}"
            </p>
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Color Palette */}
        <div className="space-y-4">
          <Card className="glass-card p-4">
            <h3 className="font-semibold text-gray-900 mb-3">🎨 Current Color</h3>
            <div 
              className="w-full h-16 rounded-lg border-4 border-white shadow-lg"
              style={{ backgroundColor: currentColor }}
            />
          </Card>

          <Card className="glass-card p-4">
            <h3 className="font-semibold text-gray-900 mb-3">🌈 Color Palettes</h3>
            <div className="space-y-3">
              {Object.entries(colorPalettes).map(([key, palette]) => (
                <div key={key} className="space-y-2">
                  <button
                    onClick={() => setSelectedPalette(key)}
                    className={`w-full text-left p-2 rounded-lg transition-all ${
                      selectedPalette === key ? 'bg-purple-50 border-2 border-purple-200' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-medium text-sm">{palette.name}</div>
                    <div className="text-xs text-gray-500">{palette.mood}</div>
                  </button>
                  {selectedPalette === key && (
                    <div className="grid grid-cols-4 gap-1">
                      {palette.colors.map((color, index) => (
                        <button
                          key={index}
                          className="aspect-square rounded border-2 border-white shadow-sm hover:scale-110 transition-transform"
                          style={{ backgroundColor: color }}
                          onClick={() => setCurrentColor(color)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Coloring Area */}
        <div className="lg:col-span-2">
          <Card className="glass-card p-6">
            <div className="relative">
              <div 
                className="w-full aspect-square max-w-lg mx-auto border-2 border-gray-200 rounded-lg bg-white cursor-pointer"
                dangerouslySetInnerHTML={{ __html: currentDesign.svg }}
                onClick={(e) => {
                  const section = e.target.getAttribute('data-section');
                  if (section !== null) {
                    colorSection(section);
                    // Apply color to the clicked element
                    e.target.style.fill = currentColor;
                  }
                }}
              />
              {completionPercentage === 0 && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-10 rounded-lg">
                  <div className="text-center text-gray-600">
                    <div className="text-4xl mb-2">{currentDesign.icon}</div>
                    <p>Click on sections to color them!</p>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Controls */}
        <div className="space-y-4">
          <Card className="glass-card p-4">
            <h3 className="font-semibold text-gray-900 mb-3">⚡ Actions</h3>
            <div className="space-y-2">
              <Button 
                onClick={resetColoring}
                variant="outline"
                className="w-full btn-secondary"
              >
                🗑️ Clear All
              </Button>
              <Button 
                onClick={() => setSelectedDesign(null)}
                variant="outline"
                className="w-full btn-secondary"
              >
                ← Back to Designs
              </Button>
              <Button 
                onClick={completeColoring}
                className="w-full btn-primary"
                disabled={completionPercentage < 25}
              >
                ✅ Complete Session
              </Button>
            </div>
          </Card>

          <Card className="glass-card p-4">
            <h3 className="font-semibold text-gray-900 mb-3">📊 Progress</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span>Sections Colored:</span>
                <span className="font-medium">
                  {Object.keys(coloredSections).length}/{currentDesign.sections}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Colors Used:</span>
                <span className="font-medium">
                  {[...new Set(Object.values(coloredSections))].length}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Time Spent:</span>
                <span className="font-medium">{formatTime(sessionTime)}</span>
              </div>
            </div>
          </Card>

          {completionPercentage >= 75 && (
            <Card className="glass-card p-4 bg-gradient-to-r from-green-50 to-amber-50">
              <div className="text-center space-y-2">
                <div className="text-2xl">🎉</div>
                <h4 className="font-semibold text-green-800">Almost Done!</h4>
                <p className="text-sm text-green-700">
                  You're doing amazing work. Take a moment to appreciate your creativity!
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ColoringTherapy;
